<template>
  <v-container fluid class="ncrm-onboarding-home">

    <!-- Page header -->
    <NcrmHomePageHeader
      icon="mdi-rocket-launch-outline"
      :title="$t('ncrm.onboarding.home.title')"
      :subtitle="$t('ncrm.onboarding.home.subtitle')"
      data-tour="onboarding-header"
    >
      <v-btn variant="text" color="primary" :loading="loading" @click="loadBoards">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("ncrm.onboarding.actions.refresh") }}
      </v-btn>
      <v-btn variant="text" color="primary" data-tour="onboarding-templates" @click="openTemplates">
        <v-icon start>mdi-shape-outline</v-icon>
        {{ $t("ncrm.onboarding.actions.manageTemplates") }}
      </v-btn>
      <v-btn variant="text" color="primary" @click="openContracts">
        <v-icon start>mdi-file-document-outline</v-icon>
        {{ $t("ncrm.onboarding.actions.openContracts") }}
      </v-btn>
      <v-btn color="primary" @click="openProductsCatalog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.onboarding.actions.createProduct") }}
      </v-btn>
    </NcrmHomePageHeader>
    <!-- KPI cards — clickable, same visual as NcrmHomeKpiCards -->
    <v-row dense class="mt-3" data-tour="onboarding-kpi">
      <v-col
        v-for="item in globalKpis"
        :key="item.key"
        cols="12"
        sm="6"
        lg="3"
      >
        <v-card
          class="ncrm-onboarding-home__kpi-card pa-3 h-100"
          :class="`ncrm-onboarding-home__kpi-card--${item.color}`"
          role="button"
          tabindex="0"
          @click="openKpiBoard(item.key)"
          @keydown.enter.prevent="openKpiBoard(item.key)"
          @keydown.space.prevent="openKpiBoard(item.key)"
        >
          <div class="d-flex align-center justify-space-between ga-2 mb-1">
            <div class="text-caption text-medium-emphasis">{{ item.label }}</div>
            <v-icon :color="item.color" size="15" style="opacity:0.7">{{ item.icon }}</v-icon>
          </div>
          <div class="text-h6 font-weight-bold ncrm-text-strong text-left">
            {{ formatNumber(item.value) }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- View tabs -->
    <div class="mt-3 d-flex align-center flex-wrap ga-2">
      <NcrmPreferredViewTabs
        v-model="activeView"
        :items="viewItems"
        :preferred-value="preferredView"
        :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
        @set-default="setPreferredView"
      />
    </div>

    <!-- Board view -->
    <template v-if="activeView === 'board'">
      <v-alert
        v-if="!loading && !boards.length"
        type="info"
        class="mt-2"
      >
        {{ $t("ncrm.onboarding.home.empty") }}
      </v-alert>

      <v-row v-else dense class="mt-2" data-tour="onboarding-boards">
        <v-col cols="12" sm="6" lg="4" v-for="board in boards" :key="board.template_id">
          <article
            class="ncrm-onboarding-home__board-card h-100"
            :class="{ 'ncrm-onboarding-home__board-card--overdue': hasOverdueTasks(board) }"
            role="button"
            tabindex="0"
            @click="openBoard(board.product_id)"
            @keydown.enter.prevent="openBoard(board.product_id)"
            @keydown.space.prevent="openBoard(board.product_id)"
          >
            <!-- Zone 1: Header — product name + status chip -->
            <div class="d-flex align-start justify-space-between ga-2">
              <div class="ncrm-onboarding-home__entry-main">
                <div class="text-body-1 ncrm-text-strong ncrm-onboarding-home__entry-title">
                  {{ resolveProductName(board) }}
                </div>
                <!-- Zone 2: Secondary identifier — template name -->
                <div class="ncrm-onboarding-home__entry-id">
                  {{ resolveTemplateName(board) }}
                </div>
              </div>
              <v-chip
                size="x-small"
                :color="isTemplateActive(board) ? 'success' : 'warning'"
                class="flex-shrink-0 ncrm-onboarding-home__entry-status"
              >
                {{ isTemplateActive(board) ? $t("ncrm.onboarding.home.board.active") : $t("ncrm.onboarding.home.board.inactive") }}
              </v-chip>
            </div>

            <!-- Zone 3: Meta properties -->
            <div class="ncrm-onboarding-home__entry-meta mt-2">
              <div class="ncrm-onboarding-home__entry-meta-row">
                <v-icon size="14" class="ncrm-onboarding-home__entry-icon">mdi-rocket-launch-outline</v-icon>
                <span>{{ $t("ncrm.onboarding.home.board.metrics.active") }}: {{ formatNumber(board.onboarding_active) }}</span>
              </div>
              <div class="ncrm-onboarding-home__entry-meta-row">
                <v-icon size="14" class="ncrm-onboarding-home__entry-icon">mdi-check-circle-outline</v-icon>
                <span>{{ $t("ncrm.onboarding.home.board.metrics.completed") }}: {{ formatNumber(board.onboarding_completed) }}</span>
              </div>
            </div>

            <!-- Zone 4: KPI chips -->
            <div class="d-flex flex-wrap ga-2 mt-2">
              <v-chip class="ncrm-onboarding-home__chip-neutral">
                {{ $t("ncrm.onboarding.home.board.metrics.total") }}: {{ formatNumber(board.onboarding_total) }}
              </v-chip>
              <v-chip color="warning">
                <v-icon start size="13">mdi-alert-circle-outline</v-icon>
                {{ $t("ncrm.onboarding.home.board.metrics.blocked") }}: {{ formatNumber(board.onboarding_blocked) }}
              </v-chip>
              <v-chip
                size="small"
                :variant="hasOverdueTasks(board) ? 'flat' : 'tonal'"
                :color="hasOverdueTasks(board) ? 'error' : 'success'"
              >
                <v-icon start size="13">
                  {{ hasOverdueTasks(board) ? "mdi-clock-alert-outline" : "mdi-clock-check-outline" }}
                </v-icon>
                {{ $t("ncrm.onboarding.home.board.metrics.overdueTasks") }}: {{ formatNumber(board.overdue_task_count) }}
              </v-chip>
            </div>
          </article>
        </v-col>
      </v-row>
    </template>

    <!-- Table view -->
    <v-card class="mt-2" v-else data-tour="onboarding-boards">
      <v-data-table
        :headers="tableHeaders"
        :items="boards"
        item-key="template_id"
        density="comfortable"
        class="elevation-0 ncrm-onboarding-home__table"
        :loading="loading"
        :row-props="tableRowProps"
        @click:row="onTableRowClick"
      >
        <template #item.product_name="{ item }">
          <div class="d-flex align-center ga-2">
            <v-icon size="14" class="ncrm-onboarding-home__table-cell-icon">mdi-package-variant-outline</v-icon>
            <div>
              <div class="text-body-2 ncrm-text-strong">{{ resolveProductName(item) }}</div>
              <div class="text-caption text-medium-emphasis">{{ resolveTemplateName(item) }}</div>
            </div>
          </div>
        </template>

        <template #item.is_active="{ item }">
          <v-chip size="x-small" :color="isTemplateActive(item) ? 'success' : 'warning'">
            {{ isTemplateActive(item) ? $t("ncrm.onboarding.home.board.active") : $t("ncrm.onboarding.home.board.inactive") }}
          </v-chip>
        </template>

        <template #item.onboarding_total="{ item }">{{ formatNumber(item.onboarding_total) }}</template>
        <template #item.onboarding_active="{ item }">{{ formatNumber(item.onboarding_active) }}</template>
        <template #item.onboarding_blocked="{ item }">{{ formatNumber(item.onboarding_blocked) }}</template>
        <template #item.onboarding_completed="{ item }">{{ formatNumber(item.onboarding_completed) }}</template>

        <template #item.overdue_task_count="{ item }">
          <span :class="{ 'ncrm-onboarding-home__metric-overdue': hasOverdueTasks(item) }">
            {{ formatNumber(item.overdue_task_count) }}
          </span>
        </template>

        <template #item.row_nav>
          <v-icon size="16" class="ncrm-onboarding-home__table-chevron">mdi-chevron-right</v-icon>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("ncrm.onboarding.home.empty") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const ONBOARDING_HOME_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
const ONBOARDING_HOME_PAGE_KEY_FALLBACK = "/ncrm/onboarding";

export default {
  name: "NcrmOnboardingHome",
  components: {
    NcrmPreferredViewTabs,
    NcrmHomePageHeader,
  },
  data() {
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, ONBOARDING_HOME_PAGE_KEY_FALLBACK),
        items: ONBOARDING_HOME_VIEW_ITEMS,
        fallback: "board",
      }) || "board";
    return {
      loading: false,
      activeView: preferredView,
      preferredView,
      boards: [],
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    viewItems() {
      return [
        { value: "board", label: this.$t("ncrm.onboarding.board.views.board") },
        { value: "table", label: this.$t("ncrm.onboarding.board.views.table") },
      ];
    },
    globalKpis() {
      const totals = this.boards.reduce(
        (accumulator, item) => {
          accumulator.active += parseNumeric(item?.onboarding_active, 0);
          accumulator.blocked += parseNumeric(item?.onboarding_blocked, 0);
          accumulator.completed += parseNumeric(item?.onboarding_completed, 0);
          accumulator.overdueTasks += parseNumeric(item?.overdue_task_count, 0);
          return accumulator;
        },
        { active: 0, blocked: 0, completed: 0, overdueTasks: 0 }
      );

      return [
        {
          key: "active",
          label: this.$t("ncrm.onboarding.home.kpi.active"),
          value: totals.active,
          icon: "mdi-rocket-launch-outline",
          color: "primary",
        },
        {
          key: "blocked",
          label: this.$t("ncrm.onboarding.home.kpi.blocked"),
          value: totals.blocked,
          icon: "mdi-alert-circle-outline",
          color: "warning",
        },
        {
          key: "overdue_tasks",
          label: this.$t("ncrm.onboarding.home.kpi.overdueTasks"),
          value: totals.overdueTasks,
          icon: "mdi-clock-alert-outline",
          color: "error",
        },
        {
          key: "completed",
          label: this.$t("ncrm.onboarding.home.kpi.completed"),
          value: totals.completed,
          icon: "mdi-check-decagram-outline",
          color: "success",
        },
      ];
    },
    tableHeaders() {
      return [
        { title: this.$t("ncrm.onboarding.home.board.productLabel"), key: "product_name" },
        { title: this.$t("ncrm.onboarding.board.columns.status"), key: "is_active", sortable: false },
        { title: this.$t("ncrm.onboarding.home.board.metrics.total"), key: "onboarding_total" },
        { title: this.$t("ncrm.onboarding.home.board.metrics.active"), key: "onboarding_active" },
        { title: this.$t("ncrm.onboarding.home.board.metrics.blocked"), key: "onboarding_blocked" },
        { title: this.$t("ncrm.onboarding.home.board.metrics.completed"), key: "onboarding_completed" },
        { title: this.$t("ncrm.onboarding.home.board.metrics.overdueTasks"), key: "overdue_task_count" },
        { title: "", key: "row_nav", sortable: false, width: "48px", align: "center" },
      ];
    },
  },
  mounted() {
    this.loadBoards();
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, ONBOARDING_HOME_PAGE_KEY_FALLBACK);
    },
    showSnackbar(message, color = "info") {
      this.snackbar = { show: true, color, message };
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: ONBOARDING_HOME_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    formatNumber(value) {
      return formatDisplayNumber(parseNumeric(value, 0));
    },
    isTemplateActive(item) {
      return Number(item?.is_active) === 1 || item?.is_active === true;
    },
    hasOverdueTasks(item) {
      return parseNumeric(item?.overdue_task_count, 0) > 0;
    },
    resolveProductName(item) {
      const name = String(item?.product_name || "").trim();
      if (name) return name;
      const productId = Number(item?.product_id || 0);
      return productId > 0 ? `#${productId}` : "-";
    },
    resolveTemplateName(item) {
      const templateName = String(item?.template_name || "").trim();
      if (templateName) return templateName;
      return this.$t("ncrm.onboarding.home.board.templateFallback");
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    async loadBoards() {
      this.loading = true;
      try {
        const { data } = await axios.get("/ncrm/onboarding/boards");
        this.boards = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm-onboarding-home] load boards failed", error);
        this.boards = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.home.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    openProductsCatalog() {
      this.$router.push({ name: "NcrmProducts" });
    },
    openContracts() {
      this.$router.push({ name: "NcrmContractsHome" });
    },
    openTemplates() {
      this.$router.push({ name: "NcrmOnboardingTemplates" });
    },
    openKpiBoard(kpiKey) {
      const normalizedKey = String(kpiKey || "").trim();
      if (!normalizedKey) return;
      this.$router.push({
        name: "NcrmOnboardingBoards",
        query: { kpi: normalizedKey },
      });
    },
    openBoard(productId) {
      const normalizedProductId = Number(productId || 0);
      if (!normalizedProductId) {
        this.showSnackbar(this.$t("ncrm.onboarding.home.messages.missingProduct"), "warning");
        return;
      }
      this.$router.push({
        name: "NcrmOnboardingBoard",
        params: { productId: String(normalizedProductId) },
      });
    },
    onTableRowClick(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item ?? payload;
      this.openBoard(row?.product_id);
    },
  },
};
</script>

<style src="./NcrmOnboardingHome.styles.css" />
